import React, { useContext, useState } from "react";

/**
 * Creates a new React Context provider that manages a piece of state, and hooks
 * to read and update that state. This can be useful to avoid prop drilling, and
 * creating the context like this can avoid undefined values in the hooks' type
 * signatures.
 *
 * @returns a tuple with three entries:
 *  - a context provider that requires an initial state argument
 *  - a state consumer hook
 *  - a state setter hook
 */
export function makeStateContext<T>() {
  const ValueContext = React.createContext<T | undefined>(undefined);
  const SetterContext = React.createContext<
    React.Dispatch<React.SetStateAction<T>> | undefined
  >(undefined);

  function Provider({
    initialValue,
    children,
  }: {
    initialValue: T;
    children: React.ReactNode;
  }) {
    const [state, setState] = useState<T>(initialValue);
    return (
      <SetterContext.Provider value={setState}>
        <ValueContext.Provider value={state}>{children}</ValueContext.Provider>
      </SetterContext.Provider>
    );
  }

  function useContextValue() {
    const value = useContext(ValueContext);
    if (value == null) {
      throw new Error("must be used within a provider");
    }
    return value;
  }

  function useSetContextValue() {
    const setter = useContext(SetterContext);
    if (setter == null) {
      throw new Error("must be used within a provider");
    }
    return setter;
  }

  return [Provider, useContextValue, useSetContextValue] as const;
}
